/* eslint-disable */
module.exports = (Swiper) => {
  class SwiperExt extends Swiper {
    constructor(...args) {
      const [selector, options = {}, ...restOfArgs] = args;
      const callOriginalHandler = (
        eventName,
        eventHandlerOptions = {},
        ...eventArgs
      ) => {
        if (typeof eventHandlerOptions[eventName] === "function") {
          eventHandlerOptions[eventName](...eventArgs);
        }
      };
      const updatedOptions = {
        ...options,
        on: {
          ...(options.on || {}),
          afterInit(...eventArgs) {
            this.extAfterInitHandler(...eventArgs);
            callOriginalHandler("afterInit", options.on, ...eventArgs);
          },
          resize(...eventArgs) {
            this.extResizeHandler(...eventArgs);
            callOriginalHandler("resize", options.on, ...eventArgs);
          },
          breakpoint(...eventArgs) {
            this.extResizeHandler(...eventArgs);
            callOriginalHandler("breakpoint", options.on, ...eventArgs);
          },
          slideNextTransitionEnd(...eventArgs) {
            this.extSlideNextTransitionEndHandler(...eventArgs);
            callOriginalHandler(
              "slideNextTransitionEnd",
              options.on,
              ...eventArgs
            );
          },
          slidePrevTransitionEnd(...eventArgs) {
            this.extSlidePrevTransitionEndHandler(...eventArgs);
            callOriginalHandler(
              "slidePrevTransitionEnd",
              options.on,
              ...eventArgs
            );
          },
          ...(options.pagination
            ? {
                paginationRender(...eventArgs) {
                  this.extOnPaginationRenderedHandler(...eventArgs);
                  callOriginalHandler(
                    "paginationRender",
                    options.on,
                    ...eventArgs
                  );
                },
              }
            : {}),
        },
      };
      super(selector, updatedOptions, ...restOfArgs);
      this.bindEventHandlers();
    }

    /**
     * Check whether to turn on functionality or no
     * @return {boolean}
     */
    _shouldExtend() {
      return (
        this.params.loop === true &&
        this.params.supports4k &&
        this.slides.length >
          (this.params.slidesPerView !== "auto"
            ? this.params.slidesPerView
            : this.slidesPerViewDynamic())
      );
    }

    /**
     * Remove unnecessary swiper classes
     * @param {Array} slides
     * @return {Array}
     */
    _removeAdditionalClassesFromSlides(slides) {
      return slides.map((slide) => {
        slide.classList.forEach((className) => {
          if (className.includes("swiper") && className !== "swiper-slide") {
            slide.classList.remove(className);
          }
        });

        return slide;
      });
    }

    _customActiveCssClass(swiper) {
      const activeSlide = swiper.slides.find((slide) =>
        slide.classList.contains("swiper-slide-active")
      );

      activeSlide.classList.add("active-slide");
    }

    /**
     * Update wrapper with slides and change position
     * @param {Swiper} swiper
     * @param {Array} updatedSlidesArr
     * @param {number} slideToIndex
     * @return {void}
     */
    _updateSlidesAndSlideTo(
      swiper,
      updatedSlidesArr,
      slideToIndex,
      isTriggeredByResize = false
    ) {
      const { el } = swiper;
      const sliderWrapperEl = el.querySelector(".swiper-wrapper");
      sliderWrapperEl.innerHTML = "";
      sliderWrapperEl.append(...updatedSlidesArr);

      let activeSlideIndex = updatedSlidesArr.findIndex((slide) =>
        slide.classList.contains("swiper-slide-active")
      );
      if (activeSlideIndex === -1) {
        activeSlideIndex = slideToIndex;
      }

      // Updating slides on resize has a small delay

      let activeSlide = updatedSlidesArr.find((slide) =>
        slide.classList.contains("active-slide")
      );
  
      if (activeSlide) {
        activeSlide.classList.remove("active-slide");
      }

      swiper.once("transitionEnd", (swiper) => {
        if (isTriggeredByResize) {
          swiper.once("transitionEnd", this._customActiveCssClass(swiper));
          swiper.slideTo(activeSlideIndex, 0, false);
        } else {
          this._customActiveCssClass(swiper);
        }
      });

      swiper.updateSlides();

      if (!isTriggeredByResize) {
        swiper.slideTo(activeSlideIndex, 0, false);
      }
    }

    /**
     * Generate clone slides
     * @param {Swiper} swiper
     * @return {Array}
     */
    _generateSlides(swiper) {
      const { slides: swiperSlides } = swiper;

      const slides = [...swiperSlides].filter(
        (slide) => typeof slide.dataset.cloneOf === "undefined"
      );
      const slidesCopy = this._removeAdditionalClassesFromSlides(slides);
      swiper.originalSlideCount = slidesCopy.length;
      const documentWidth = document.body.clientWidth;
      const slideWidth = slides[0]?.clientWidth || 0;
      const numberOfSlidesAvailable = slides.length;
      const numberOfSlidesToFillScreen = Math.ceil(documentWidth / slideWidth);
      let totalNumberOfSlides = numberOfSlidesToFillScreen;

      // Make sure to have at least 5x slides than required to fill the screen
      // This is required to prevent empty spaces when dragging in larger screens
      while (totalNumberOfSlides / numberOfSlidesAvailable < 5) {
        totalNumberOfSlides += numberOfSlidesAvailable;
      }

      const updatedSlidesArr = [...slidesCopy];
      let numberOfElementsPrepended = 0,
        numberOfElementsAppended = 0;

      while (updatedSlidesArr.length < totalNumberOfSlides) {
        const elementsToPrepend = [...slidesCopy].map((el, idx) => {
          const cloned = el.cloneNode(true);
          cloned.dataset.cloneOf = idx;
          if (swiper.params.enableSwiperExtDebug) {
            cloned.style.backgroundColor = "red";
          }
          return cloned;
        });
        numberOfElementsPrepended += elementsToPrepend.length;
        updatedSlidesArr.unshift(...elementsToPrepend);

        const elementsToAppend = [...slidesCopy].map((el, idx) => {
          const cloned = el.cloneNode(true);
          cloned.dataset.cloneOf = idx;
          if (swiper.params.enableSwiperExtDebug) {
            cloned.style.backgroundColor = "blue";
          }
          return cloned;
        });
        numberOfElementsAppended += elementsToAppend.length;
        updatedSlidesArr.push(...elementsToAppend);
      }

      swiper.numberOfSlidesAddedToStart = numberOfElementsPrepended;
      swiper.numberOfSlidesAddedToEnd = numberOfElementsAppended;

      return updatedSlidesArr;
    }

    /**
     * Remove last slide and append back to the beginning
     * @param {Swiper} swiper
     * @return {void}
     */
    _shiftLastSlideToStart(swiper, numberOfSlides = 1) {
      const { slides, activeIndex } = swiper;
      // To prevent moving first slide when going backward for stabilization after dragging forwards
      if (
        numberOfSlides === 1 &&
        slides[slides.length - 1].getBoundingClientRect().x <=
          document.body.clientWidth
      ) {
        return;
      }
      const slidesCopy = [...slides];
      const updatedSlidesArr = [...slidesCopy];
      //To shift the last numberOfSlides elements to the front of the array.
      updatedSlidesArr.unshift(...updatedSlidesArr.splice(-numberOfSlides));

      this._updateSlidesAndSlideTo(
        swiper,
        updatedSlidesArr,
        activeIndex + numberOfSlides
      );
    }

    /**
     * Remove first slide and append back to the end
     * @param {Swiper} swiper
     * @return {void}
     */
    _shiftFirstSlideToEnd(swiper, numberOfSlides = 1) {
      const { slides, activeIndex } = swiper;
      // To prevent moving first slide when going forward for stabilization after dragging backwards
      const firstSlideBoundingRect = slides[0].getBoundingClientRect();
      if (
        numberOfSlides === 1 &&
        firstSlideBoundingRect.x + firstSlideBoundingRect.width >= 0
      ) {
        return;
      }
      const slidesCopy = [...slides];
      const updatedSlidesArr = [...slidesCopy];
      updatedSlidesArr.push(...updatedSlidesArr.splice(0, numberOfSlides));

      this._updateSlidesAndSlideTo(
        swiper,
        updatedSlidesArr,
        activeIndex - numberOfSlides
      );
    }

    /**
     * Slider resize and breakpoints events handler
     * @param {Swiper} swiper
     * @return {void}
     */
    extResizeHandler(swiper) {
      if (!this._shouldExtend()) {
        return;
      }

      const { realIndex } = swiper;

      const updatedSlidesArr = this._generateSlides(swiper);
      let newActiveIndex = updatedSlidesArr.findIndex(
        (slide) =>
          `${slide.dataset.swiperSlideIndex}` === `${realIndex}` &&
          typeof slide.dataset.cloneOf === "undefined"
      );
      this._updateSlidesAndSlideTo(
        swiper,
        updatedSlidesArr,
        newActiveIndex,
        true
      );
    }

    /**
     * Handle afterInit event
     * Clone slides until page width fills up
     * @param {Swiper} swiper
     * @return {void}
     */
    extAfterInitHandler(swiper) {
      if (!this._shouldExtend()) {
        return;
      }

      const updatedSlidesArr = this._generateSlides(swiper);

      const numberOfSlidesToShift = swiper.originalSlideCount > 2 ? 2 : 0; // NOSONAR
      const newActiveIndex =
        swiper.numberOfSlidesAddedToStart + swiper.activeIndex;

      this._updateSlidesAndSlideTo(swiper, updatedSlidesArr, newActiveIndex);
    }

    /**
     * Handle previous transition end
     * @param {Swiper} swiper
     * @return {void}
     */
    extSlidePrevTransitionEndHandler(swiper) {
      if (!this._shouldExtend()) {
        return;
      }

      const { previousIndex, activeIndex } = swiper;
      const numberOfSlidesChanged = Math.abs(previousIndex - activeIndex);
      this._shiftLastSlideToStart(swiper, numberOfSlidesChanged);
      delete swiper.beforeTransitionRealIndex;
    }

    /**
     * Handle next transition end
     * @param {Swiper} swiper
     * @return {void}
     */
    extSlideNextTransitionEndHandler(swiper) {
      if (!this._shouldExtend()) {
        return;
      }

      const { previousIndex, activeIndex } = swiper;
      const numberOfSlidesChanged = Math.abs(previousIndex - activeIndex);
      this._shiftFirstSlideToEnd(swiper, numberOfSlidesChanged);
      delete swiper.beforeTransitionRealIndex;
    }

    /**
     * Handle pagination rendered
     * Remove extra bullets
     * @param {Swiper} swiper
     * @param {HTMLElement} paginationEl
     * @return {void}
     */
    extOnPaginationRenderedHandler(swiper, paginationEl) {
      if (!this._shouldExtend()) {
        return;
      }

      Array.from(paginationEl.children).forEach((el, index) => {
        if (index >= swiper.originalSlideCount) {
          el.remove();
        } else {
          el.onclick = (event) =>
            this._onPaginationBulletClickHandler(event, swiper);
        }
      });
    }

    _findClosestNumber(arr, target, forward = true) {
      const filteredArr = arr.filter((num) =>
        forward ? num > target : num < target
      );
      if (!filteredArr.length) {
        return null;
      }
      const closestNumber = filteredArr.reduce((prev, curr) => {
        const prevDiff = Math.abs(prev - target);
        const currDiff = Math.abs(curr - target);
        return currDiff < prevDiff ? curr : prev;
      });

      return closestNumber;
    }

    _onPaginationBulletClickHandler(event, swiper) {
      event.preventDefault();
      event.stopPropagation();

      const { slides, activeIndex } = swiper;

      const bulletEl = event.currentTarget;
      const bulletRealIndex = Array.from(bulletEl.parentNode.children).indexOf(
        bulletEl
      );
      const activeSlide = Array.from(swiper.slides).find((slide) =>
        slide.classList.contains("swiper-slide-active")
      );
      if (!activeSlide) {
        return;
      }
      const candidateElementIndexes = [];
      Array.from(slides).forEach((slide, index) => {
        if (parseInt(slide.dataset.swiperSlideIndex) === bulletRealIndex) {
          candidateElementIndexes.push(index);
        }
      });
      const shouldSearchForward =
        parseInt(activeSlide.dataset.swiperSlideIndex) < bulletRealIndex;
      const candidateElementIndex = shouldSearchForward
        ? this._findClosestNumber(candidateElementIndexes, activeIndex)
        : this._findClosestNumber(candidateElementIndexes, activeIndex, false);
      if (candidateElementIndex === null) {
        return;
      }
      swiper.slideTo(candidateElementIndex);
    }
    _getElementTranslate3dX(element) {
      if (!element) {
        console.error("Invalid element provided.");
        return null;
      }
      // Get the computed style of the element
      const style = window.getComputedStyle(element);
      // Get the transform property
      const transform =
        style.transform || style.webkitTransform || style.mozTransform; // NOSONAR
      if (transform && transform !== "none") {
        // Extract the matrix values
        const matrix = transform.match(/matrix.*\((.+)\)/)[1].split(", "); // NOSONAR
        let x;
        if (matrix.length === 6) {
          // 'matrix(a, b, c, d, tx, ty)' -> 2D transform
          x = parseFloat(matrix[4]);
        } else if (matrix.length === 16) {
          // 'matrix3d(a1, b1, c1, d1, a2, b2, c2, d2, a3, b3, c3, d3, tx, ty, tz, d4)' -> 3D transform
          x = parseFloat(matrix[12]);
        }
        return Math.round(x);
      }
      console.log("No transform applied to the element.");
      return 0; // Default to 0 if no transform is applied
    }
    _fillEdgeGaps() {
      const slideWidth = this.slides[0].getBoundingClientRect().width;
      const { x: wrapperXVal } = this.wrapperEl.getBoundingClientRect();
      if (wrapperXVal > 0) {
        // Left edge gap
        const numberOfSlidesRequired = Math.ceil(
          Math.abs(wrapperXVal) / slideWidth
        );
        this._shiftLastSlideToStart(this, numberOfSlidesRequired + 2);
      }
    }
    bindEventHandlers() {
      if (!this._shouldExtend()) {
        return;
      }
      this.wrapperEl.addEventListener("transitionend", () => {
        if (this.transitionEndTimeout) {
          clearTimeout(this.transitionEndTimeout);
        }
        this.transitionEndTimeout = setTimeout(() => {
          this._fillEdgeGaps();
          delete this.transitionEndTimeout;
        }, 100);
      });
    }
  }
  return SwiperExt;
};
/* eslint-enable */
